import { useEffect, useContext, useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import accountCreatedSchema from "../schema/accountCreated";
import InputField from "../../common/InputField";
import { theme } from "../../theme";
import BtnSpinner from "../../common/BtnSpinner";
import { Context } from "../Provider";
import PhoneNumberInputField from "./accountCreated/PhoneNumberInputField";
import SelectorGenderField from "./accountCreated/SelectorGenderField";
import SelectorShirtSizeField from "./accountCreated/SelectorShirtSizeField";
import inputFieldSize from "./accountCreated/inputFieldSize";
import { inputFieldsDOB, inputFieldsMid, inputFieldsPassword, inputFieldsTop } from "./accountCreated/inputFields";
import { submitRegistration, temporaryLogin } from "../service";
import { setFtm } from "../../storage/ftm";
import Otp from "./accountCreated/Otp";
import Error from "./accountCreated/Error";
import CardTemplate from "../../common/CardTemplate";

const AccountCreated = () => {
    const navigate = useNavigate();

    const { state, updateCurrentStep } = useContext(Context);
    const { ftm, step } = state;
    const readOnly = step?.stepId > 1;

    const [isLoading, setIsLoading] = useState(false);
    const [openOTP, setOpenOTP] = useState(false);
    const [showError, setShowError] = useState(false);
    const [error, setError] = useState(null);

    const {
        handleSubmit,
        formState: { errors },
        control,
        register,
    } = useForm({
        resolver: yupResolver(accountCreatedSchema),
        defaultValues: ftm,
    });

    const handleOpenOTPModal = () => setOpenOTP(true);
    const handleCloseOTPModal = () => setOpenOTP(false);

    const handleErrorClose = () => {
        setError(null);
        setShowError(false);
    };

    useEffect(() => {
        if (step?.stepId === 1) {
            handleOpenOTPModal();
        }
    }, [step?.stepId]);


    const renderInputFields = (fields, className = "col-md-6") =>
        fields.map(({ name, label, placeholder, type }, idx) => (
            <div className={`col-12 ${className}`} key={idx}>
                <InputField
                    field={{ name, label, placeholder, size: inputFieldSize, type }}
                    register={register}
                    errors={errors}
                    readOnly={readOnly}
                />
            </div>
        ));

    const onSubmit = async (data) => {
        setIsLoading(true);

        try {
            const response = await submitRegistration(data);

            if (response?.error) {
                setError(response.error);
                setShowError(true);
            } else {
                const accessKey = response.key;
                const temporaryToken = response.temporary_token;

                const temporaryTokenResponse = await temporaryLogin(temporaryToken);
                if (temporaryTokenResponse.status === "Success") {
                    // Handle successful login
                    setFtm({
                        ftm_id: temporaryTokenResponse?.ftm_id,
                        temporary_token: temporaryTokenResponse?.temporary_token,
                        token: temporaryTokenResponse?.token,
                        refreshToken: temporaryTokenResponse?.refreshToken
                    });

                    handleOpenOTPModal();
                    updateCurrentStep(1);

                    navigate(`/${accessKey}`);
                }
            }
        } catch (error) {
            console.error("Registration failed:", error);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <CardTemplate
            stepId={1}
            currentStep={step?.stepId}
            body={
                <>
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <div className="row">{renderInputFields(inputFieldsTop)}</div>

                        <div className="row mt-3">
                            {renderInputFields(inputFieldsMid)}
                            <PhoneNumberInputField control={control} errors={errors} readOnly={readOnly} />
                        </div>

                        <div className="row mt-3">
                            {renderInputFields(inputFieldsDOB, "col-md-4")}
                            <div className="col-12 col-md-4">
                                <SelectorGenderField register={register} errors={errors} readOnly={readOnly} />
                            </div>
                            <div className="col-12 col-md-4">
                                <SelectorShirtSizeField setIsLoading={setIsLoading} register={register} errors={errors} readOnly={readOnly} />
                            </div>
                        </div>

                        <div className="row mt-3">
                            {renderInputFields(inputFieldsPassword)}
                        </div>
                        {step?.stepId === 0 && (
                            <div className="mt-5">
                                <div className="d-flex align-items-center mb-sm-0 mb-4">
                                    <div className="w-50">
                                        <h5>Create Your Account</h5>
                                        <p className="text-sm mb-0">
                                            Welcome aboard!. This won't take long.
                                        </p>
                                    </div>
                                    <div className="w-50 text-end">
                                        <button
                                            className={`btn bg-gradient-${theme.main} mb-0 ms-2 btn-sm`}
                                            type="submit"
                                            name="button"
                                            disabled={isLoading}
                                        >
                                            {isLoading ? <BtnSpinner /> : "Save and Continue"}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </form>

                    <Otp show={openOTP} handleClose={handleCloseOTPModal} />
                    <Error show={showError} error={error} handleClose={handleErrorClose} />
                </>
            }
        />
    );
};

export default AccountCreated;